/**
 * Dashboard utility functions and constants - separated to avoid circular dependencies
 */
import { find, forEach, get, includes, isEmpty, isNumber, set } from 'lodash-es';
import type { Company } from '../store/companies/types';
import { DynamicObject } from '../utils/commonInterfaces';

// Display type values
export const widgetDisplayTypeValues = {
    TABLE: 'Table',
    PIE_CHART: 'Pie chart',
    BAR_CHART: 'Bar chart',
    LINE_CHART: 'Line chart'
};

// Chart widget display types - used by widget fields
export const chartWidgetDisplayTypes = [
    {
        label: 'Table',
        value: widgetDisplayTypeValues.TABLE,
    },
    {
        label: 'Pie chart',
        value: widgetDisplayTypeValues.PIE_CHART,
    },
    {
        label: 'Bar chart',
        value: widgetDisplayTypeValues.BAR_CHART,
    }
];

export const chartWidgetDisplayNoPieTypes = [
    {
        label: 'Table',
        value: widgetDisplayTypeValues.TABLE,
    },
    {
        label: 'Bar chart',
        value: widgetDisplayTypeValues.BAR_CHART,
    }
];

export const historicalChartWidgetDisplayTypes = [
    {
        label: 'Table',
        value: widgetDisplayTypeValues.TABLE,
    },
    {
        label: 'Pie chart',
        value: widgetDisplayTypeValues.PIE_CHART,
    },
    {
        label: 'Bar chart',
        value: widgetDisplayTypeValues.BAR_CHART,
    },
    {
        label: 'Line chart',
        value: widgetDisplayTypeValues.LINE_CHART,
    }
];

// Common form fields
export const commonOrgFormFields = {
    REGION: 'Region',
    COUNTRIES: 'Countries',
    COMPANIES: 'Companies',
};

/**
 * Function for getting the region options.
 */
export const getRegionOptions = (organisationCompanies: Company[]) => {
    const regions: DynamicObject[] = [];
    forEach(organisationCompanies, (uc: DynamicObject) => {
        if (!find(regions, ['value', uc.Region]) && uc.Region) {
            regions.push({
                label: uc.Region,
                value: uc.Region,
            });
        }
    });
    return regions;
};

/**
 * Function for cleaning tree select with all payload.
 */
export const cleanTreeSelectWithAllPayload = (payload: string[]): string[] => {
    if (isEmpty(payload)) return [];
    return payload.filter((item) => item !== 'all');
};

/**
 * Function for populating the organisation regional dashboard widgets payload
 */
export const populatePayloadForOrganisationRegionalWidgets = ({
    payload,
    widgetDetails,
    organisationCompanies,
    organisationCurrenciesAll,
    currencyCodeUsed,
    orgUserCompanies: orgUserCompaniesParam,
}: {
    payload: DynamicObject;
    widgetDetails: DynamicObject;
    organisationCompanies: Company[];
    organisationCurrenciesAll?: DynamicObject[];
    currencyCodeUsed?: string;
    orgUserCompanies?: DynamicObject[];
}) => {
    // Fallback to organisationCompanies if orgUserCompanies is not provided
    const orgUserCompanies = orgUserCompaniesParam || organisationCompanies;
    const regionPayload: string | undefined = get(
        widgetDetails,
        commonOrgFormFields.REGION
    );
    let countriesPayload: string[] = cleanTreeSelectWithAllPayload(
        get(widgetDetails, commonOrgFormFields.COUNTRIES, [])
    );
    let companyIdsPayload: string[] = cleanTreeSelectWithAllPayload(
        get(widgetDetails, commonOrgFormFields.COMPANIES, [])
    );

    let regionsValueUsed: string[] = regionPayload ? [regionPayload] : [];
    const currencyObjSel = find(organisationCurrenciesAll, [
        'Value',
        currencyCodeUsed,
    ]);

    if (isEmpty(regionsValueUsed)) {
        const regionsList = getRegionOptions(organisationCompanies);
        const firstRegionValue = get(regionsList, `0.value`);
        regionsValueUsed = firstRegionValue ? [firstRegionValue] : [];
    }

    if (isEmpty(countriesPayload) || isEmpty(companyIdsPayload)) {
        const allCountries: string[] = [];
        const allCompanies: string[] = [];

        forEach(orgUserCompanies, (uc: DynamicObject) => {
            if (
                isEmpty(countriesPayload) &&
                !includes(allCountries, uc.Country) &&
                includes(regionsValueUsed, uc.Region) &&
                uc.Country
            ) {
                allCountries.push(uc.Country);
            }

            if (
                isEmpty(companyIdsPayload) &&
                !includes(allCompanies, uc.CompanyId) &&
                includes(regionsValueUsed, uc.Region)
            ) {
                allCompanies.push(uc.CompanyId);
            }
        });

        if (isEmpty(countriesPayload)) countriesPayload = allCountries;
        if (isEmpty(companyIdsPayload)) companyIdsPayload = allCompanies;
    }

    const updatedPayload = {
        ...payload,
        Region: get(regionsValueUsed, '0'),
        Countries: countriesPayload,
        CompanyIds: companyIdsPayload,
        CurrencyCode: currencyCodeUsed,
        ConversionCurrency: get(currencyObjSel, 'Currency'),
    };

    return updatedPayload;
};

/**
 * Function for formatting the table currencies.
 * @param dataSource
 * @param formatNumber - number formatter function
 */
export const tableNumberFormatter = (dataSource: any, formatNumber: any) => {
    dataSource.map((item: any) => {
        for (const property in item) {
            if (isNumber(item[property])) {
                set(item, property, formatNumber(item[property]));
            }
        }
    });

    return dataSource;
};

/**
 * Function for formatting the table currencies.
 * @param dataSource
 * @param formatCurrency - currency formatter function
 */
export const tableCurrencyFormatter = (dataSource: any, formatCurrency: any) => {
    dataSource.map((item: any) => {
        for (const property in item) {
            if (isNumber(item[property])) {
                set(item, property, formatCurrency(item[property]));
            }
        }
    });

    return dataSource;
};

/**
 * Keys that are not related to the query for dashboard widgets
 */
export const notQueryRelatedKeys = [
    'h',
    'i',
    'minH',
    'minW',
    'moved',
    'static',
    'maxH',
    'maxW',
    'title',
    'type',
    'w',
    'x',
    'y',
];

/**
 * Widget types that display region title
 */
export const dashboardWidgetsRegionTitle = [
    'ORGANISATION_TOP_INVOICES',
    'ORGANISATION_TOP_CUSTOMERS',
    'ORGANISATION_TASKS',
    'ORGANISATION_OPEN_SUPPORT_TICKETS',
    'ORGANISATION_TICKETS',
    'ORGANISATION_TICKETS_FOR_USERS',
    'CUSTOMER_PAYMENT_BEHAVIOUR_INSIGHTS',
    'ORGANISATION_CUSTOMER_INSIGHTS_HISTORY',
    'ORGANISATION_DAILY_SALES_OUT',
    'ORGANISATION_CASHFLOW_FORECASTING',
    'ORGANISATION_PAYMENTS_ALLOCATION',
    'ORGANISATION_CURRENT_ATB',
    'ORGANISATION_NOTIFICATIONS',
    'ORGANISATION_TOTAL_OWED',
    'ORGANISATION_INVOICING_AND_SETTLEMENTS',
    'ORGANISATION_NOTIFICATION_SUCCESS',
    'ORGANISATION_SALES_VIEW',
    'ORGANISATION_CASH_ALLOCATION_STATISTICS'
];

/**
 * Widget types that display currency title
 */
export const dashboardWidgetsCurrencyTitle = [
    'ORGANISATION_TOP_INVOICES',
    'ORGANISATION_TOP_CUSTOMERS',
    'ORGANISATION_DAILY_SALES_OUT',
    'ORGANISATION_CASHFLOW_FORECASTING',
    'ORGANISATION_PAYMENTS_ALLOCATION',
    'ORGANISATION_CURRENT_ATB',
    'ORGANISATION_NOTIFICATIONS',
    'ORGANISATION_TOTAL_OWED',
    'ORGANISATION_INVOICING_AND_SETTLEMENTS',
    'ORGANISATION_NOTIFICATION_SUCCESS',
    'ORGANISATION_SALES_VIEW',
    'ORGANISATION_CASH_ALLOCATION_STATISTICS'
];

/**
 * Cash allocation widget constants
 */
export const cashAllocationActionTypes = {
    PAYMENT_ALLOCATED: 'ResourceLink',
    REMITTANCE_PROCESSED: 'RemittanceProcessed',
    PAYMENT_VERIFIED: 'PaymentVerified'
};

export const cashAllocationWidgetLabels: Record<string, string> = {
    'ResourceLink' : 'Payment Allocated',
    'PaymentVerified' : 'Payment Verified',
    'RemittanceProcessed' : 'Remittance Processed'
};

/**
 * Widget date range values
 */
export const widgetDateRangeValues = {
    SENT_ANYTIME: 'SentAnytime',
    THIS_CALENDAR_YEAR: 'ThisCalendarYear',
    LAST_CALENDAR_YEAR: 'LastCalendarYear',
    LAST_SIX_MONTHS: 'LastSixMonths',
    THIS_FINANCIAL_YEAR_AU: 'ThisFinancialYearAU',
    LAST_FINANCIAL_YEAR_AU: 'LastFinancialYearAU',
    CUSTOM_DATE_RANGE: 'CustomDateRange',
};

/**
 * Sales date range options
 */
export const salesDateRangeOptions = [
    {
        label: 'This calendar year',
        value: widgetDateRangeValues.THIS_CALENDAR_YEAR,
    },
    {
        label: 'Last calendar year',
        value: widgetDateRangeValues.LAST_CALENDAR_YEAR,
    },
    {
        label: 'This financial year (AU)',
        value: widgetDateRangeValues.THIS_FINANCIAL_YEAR_AU,
    },
    {
        label: 'Last financial year (AU)',
        value: widgetDateRangeValues.LAST_FINANCIAL_YEAR_AU,
    },
    {
        label: 'Custom date range',
        value: widgetDateRangeValues.CUSTOM_DATE_RANGE,
    },
];

/**
 * Sales date range options complete (with Last 6 months)
 */
export const salesDateRangeOptionsComplete = [
    {
        label: 'This calendar year',
        value: widgetDateRangeValues.THIS_CALENDAR_YEAR,
    },
    {
        label: 'Last calendar year',
        value: widgetDateRangeValues.LAST_CALENDAR_YEAR,
    },
    {
        label: 'Last 6 months',
        value: widgetDateRangeValues.LAST_SIX_MONTHS,
    },
    {
        label: 'This financial year (AU)',
        value: widgetDateRangeValues.THIS_FINANCIAL_YEAR_AU,
    },
    {
        label: 'Last financial year (AU)',
        value: widgetDateRangeValues.LAST_FINANCIAL_YEAR_AU,
    },
    {
        label: 'Custom date range',
        value: widgetDateRangeValues.CUSTOM_DATE_RANGE,
    },
];

/**
 * Group by source types options
 */
export const groupBySourceTypesOptions = [
    { label: 'All Sources', value: undefined },
    { label: 'Manual', value: 'Manual' },
    { label: 'Import', value: 'Import' },
    { label: 'Integration', value: 'Integration' },
    { label: 'Customer self service', value: 'CustomerSelfService' },
];

/**
 * Schedule type options
 */
export const scheduleTypeOptions = [
    { label: 'Daily', value: 'Daily' },
    { label: 'Weekly', value: 'Weekly' },
    { label: 'Monthly', value: 'Monthly' },
];

/**
 * Schedule timing options
 */
export const scheduleTimingOptions = [
    { label: 'Morning', value: 'Morning' },
    { label: 'Afternoon', value: 'Afternoon' },
    { label: 'Evening', value: 'Evening' },
];

/**
 * Schedule monthly timing options
 */
export const scheduleMonthlyTimingOptions = [
    { label: 'First of the month', value: 'FirstOfTheMonth' },
    { label: 'Last of the month', value: 'LastOfTheMonth' },
];

/**
 * Notification success date range options
 */
export const notificationSuccessDateRangeOptions = [
    {
        label: 'Sent anytime',
        value: widgetDateRangeValues.SENT_ANYTIME,
    },
    {
        label: 'This calendar year',
        value: widgetDateRangeValues.THIS_CALENDAR_YEAR,
    },
    {
        label: 'Last calendar year',
        value: widgetDateRangeValues.LAST_CALENDAR_YEAR,
    },
    {
        label: 'This financial year (AU)',
        value: widgetDateRangeValues.THIS_FINANCIAL_YEAR_AU,
    },
    {
        label: 'Last financial year (AU)',
        value: widgetDateRangeValues.LAST_FINANCIAL_YEAR_AU,
    },
    {
        label: 'Custom date range',
        value: widgetDateRangeValues.CUSTOM_DATE_RANGE,
    },
];

/**
 * Sales display options
 */
export const salesDisplayOptionValues = {
    BOTH: 'Both',
    INVOICE: 'Invoice',
    CREDIT: 'Credit',
};

export const salesDisplayOptions = [
    {
        label: 'Both',
        value: salesDisplayOptionValues.BOTH,
    },
    {
        label: 'Invoice',
        value: salesDisplayOptionValues.INVOICE,
    },
    {
        label: 'Credit',
        value: salesDisplayOptionValues.CREDIT,
    },
];

/**
 * Sales grouping options
 */
export const salesGroupingOptions = [
    { label: 'Monthly', value: true },
    { label: 'Weekly', value: false },
];

/**
 * Sales view options
 */
export const salesViewOptions = [
    { label: 'Create date', value: true },
    { label: 'Due date', value: false },
];

/**
 * Tickets widget display status options
 */
export const ticketWidgetDisplayStatusOptions = [
    { label: 'All', value: 'All' },
    { label: 'Open', value: 'Open' },
    { label: 'In progress', value: 'InProgress' },
    { label: 'Closed', value: 'Closed' },
];

export const ticketWidgetDisplayStatusOptionValues = {
    ALL: 'All',
    OPEN: 'Open',
    IN_PROGRESS: 'InProgress',
    CLOSED: 'Closed',
};

/**
 * Tickets widget group options
 */
export const ticketWidgetGroupOptions = [
    { label: 'Priority', value: 'Priority' },
    { label: 'Assignee', value: 'Assignee' },
];

export const ticketWidgetGroupOptionValues = {
    PRIORITY: 'Priority',
    ASSIGNEE: 'Assignee',
};

/**
 * Display column options for tickets widget
 */
export const displayColumnOptions = {
    OpenSum: {
        label: 'Open Tickets',
        value: 'OpenSum',
        defaultChecked: true,
        OrgViewOnly: false,
    },
    ClosedSum: {
        label: 'Closed Tickets',
        value: 'ClosedSum',
        defaultChecked: true,
        OrgViewOnly: false,
    },
};

/**
 * Current ATB view options
 */
export const currentATBViewOptions = [
    { label: 'Create date', value: true },
    { label: 'Due date', value: false },
];

/**
 * ATB and invoicing view options
 */
export const atbAndInvoicingViewOptions = [
    { label: 'Due date', value: false },
];

/**
 * Dashboard row height
 */
export const dashboardRowHeight = 80;

/**
 * Filter by user types options
 */
export const filterByUserTypesOptions = [
    {
        label: 'Company Users',
        value: 'InternalUsers',
        defaultChecked: true,
        OrgViewOnly: false,
    },
    {
        label: 'External Users',
        value: 'ExternalUsers',
        defaultChecked: true,
        OrgViewOnly: false,
    },
];

/**
 * Group by user types options
 */
export const groupByUserTypesOptions = [
    {
        label: 'User',
        value: 'User'
    },
    {
        label: 'User Type',
        value: 'UserType'
    },
];

/**
 * Flex container widgets - widgets that use flex containers
 */
export const FLEX_CONTAINER_WIDGETS = [
    'OPEN_SUPPORT_TICKETS',
    'TASKS',
    'TASKS_FOR_USERS',
    'TASK_COUNT_ACTIONED_BY_USER',
    'SALES',
    'CHANGE_STATISTICS',
    'INVOICING_AND_SETTLEMENTS',
    'TICKETS',
    'TICKETS_FOR_USERS',
    'TOTAL_OWED',
    'NOTIFICATIONS',
    'CURRENT_ATB',
    'OPEN_INVOICES',
    'COMMUNICATION_DELIVERY',
    'CUSTOMERS_SETTLEMENT',
    'DAILY_SALES_OUT',
    'CUSTOMER_PAYMENT_BEHAVIOUR_INSIGHTS',
    'CASH_ALLOCATION_STATISTICS',
    'CUSTOMER_INSIGHTS_HISTORY',
    'PAYMENTS_ALLOCATION',
    'NOTIFICATION_SUCCESS',
    //Organisation section
    'ORGANISATION_TOTAL_OWED',
    'ORGANISATION_SALES_VIEW',
    'ORGANISATION_CHANGE_STATISTICS',
    'ORGANISATION_INVOICING_AND_SETTLEMENTS',
    'ORGANISATION_NOTIFICATIONS',
    'ORGANISATION_CURRENT_ATB',
    'ORGANISATION_TASKS',
    'ORGANISATION_TASKS_FOR_USERS',
    'ORGANISATION_TASK_COUNT_ACTIONED_BY_USER',
    'ORGANISATION_OPEN_SUPPORT_TICKETS',
    'ORGANISATION_TICKETS',
    'ORGANISATION_TICKETS_FOR_USERS',
    'ORGANISATION_OPEN_INVOICES',
    'ORGANISATION_COMMUNICATION_DELIVERY',
    'ORGANISATION_CUSTOMERS_SETTLEMENT',
    'ORGANISATION_DAILY_SALES_OUT',
    'ORGANISATION_CUSTOMER_PAYMENT_BEHAVIOUR_INSIGHTS',
    'ORGANISATION_CUSTOMER_INSIGHTS_HISTORY'
];

/**
 * Dynamic container widgets - widgets with dynamic display types
 */
export const DYNAMIC_CONTAINER_WIDGETS: Record<string, { displayType: string }> = {
    NOTIFICATION_SUCCESS: {
        displayType: widgetDisplayTypeValues.PIE_CHART,
    },
    ORGANISATION_NOTIFICATION_SUCCESS: {
        displayType: widgetDisplayTypeValues.PIE_CHART,
    },
};

/**
 * Full style widgets
 */
export const FULL_STYLE_WIDGETS = [
    'OPEN_SUPPORT_TICKETS',
    'TOTAL_OWED',
    'OPEN_INVOICES',
    'DAILY_SALES_OUT',

    // Organisation section
    'ORGANISATION_TOTAL_OWED',
    'ORGANISATION_OPEN_SUPPORT_TICKETS',
    'ORGANISATION_OPEN_INVOICES',
];

/**
 * Valid student portal widget types
 */
export const VALID_STUDENT_PORTAL_WIDGET_TYPES: string[] = [
    "TOP_INVOICES",
    "CUSTOMERS_SETTLEMENT",
    "OPEN_SUPPORT_TICKETS",
    "TOTAL_OWED",
    "OPEN_INVOICES",
    "PAYMENTS",
    "PAYMENTS_ALLOCATION",
    "TOP_CREDITS"
];

/**
 * All options selected key for tree select
 */
export const allOptionsSelectedKey = 'all';

/**
 * Function for populating the organisation dashboard widgets payload
 * @param payload - The payload object
 * @param widgetDetails - Widget details containing filter values
 * @param orgUserCompanies - Organisation user companies (optional, for refactored usage)
 */
export const populatePayloadForOrganisationWidgets = ({
    payload,
    widgetDetails,
    orgUserCompanies,
}: {
    payload: DynamicObject;
    widgetDetails: DynamicObject;
    orgUserCompanies?: DynamicObject[];
}) => {
    const REGIONS = 'Regions';
    const COUNTRIES = 'Countries';
    const COMPANIES = 'Companies';
    
    let regionsPayload: string[] = cleanTreeSelectWithAllPayload(
        get(widgetDetails, REGIONS, [])
    );
    let countriesPayload: string[] = cleanTreeSelectWithAllPayload(
        get(widgetDetails, COUNTRIES, [])
    );
    let companyIdsPayload: string[] = cleanTreeSelectWithAllPayload(
        get(widgetDetails, COMPANIES, [])
    );

    if (
        (isEmpty(regionsPayload) ||
        isEmpty(countriesPayload) ||
        isEmpty(companyIdsPayload)) &&
        orgUserCompanies
    ) {
        const allRegions: string[] = [];
        const allCountries: string[] = [];
        const allCompanies: string[] = [];

        forEach(orgUserCompanies, (uc: DynamicObject) => {
            if (
                isEmpty(regionsPayload) &&
                !includes(allRegions, uc.Region) &&
                uc.Region
            )
                allRegions.push(uc.Region);

            if (
                isEmpty(countriesPayload) &&
                !includes(allCountries, uc.Country) &&
                uc.Country
            )
                allCountries.push(uc.Country);

            if (
                isEmpty(companyIdsPayload) &&
                !includes(allCompanies, uc.CompanyId) &&
                uc.CompanyId
            )
                allCompanies.push(uc.CompanyId);
        });

        if (isEmpty(regionsPayload)) regionsPayload = allRegions;
        if (isEmpty(countriesPayload)) countriesPayload = allCountries;
        if (isEmpty(companyIdsPayload)) companyIdsPayload = allCompanies;
    }

    const updatedPayload = {
        ...payload,
        Regions: !isEmpty(regionsPayload) ? regionsPayload : undefined,
        Countries: !isEmpty(countriesPayload) ? countriesPayload : undefined,
        CompanyIds: !isEmpty(companyIdsPayload) ? companyIdsPayload : undefined,
    };

    return updatedPayload;
};
